import { appearancePreferencesSchema, type AppearancePreferences, type UserPreferences } from '@app/shared';
import { api, OfflineError } from './api.ts';
import { getPrefs, updatePrefs, type DevicePrefs } from './prefs.ts';

/**
 * Personal appearance is saved with the account so it follows the person to
 * every device. Changes apply on this device immediately; saving to the
 * server is retried after reconnecting, and a newer saved copy from another
 * device replaces this device's copy on the next sign-in or refresh.
 */

const accountFields = (a: DevicePrefs['appearance']): AppearancePreferences => {
  const { headerPhotoFileId: _deviceOnly, ...rest } = a;
  return rest;
};

let chain: Promise<void> = Promise.resolve();

/** Save the device's appearance to the account if it has unsaved changes. Serialized; safe to call often. */
export function saveAppearance(): Promise<void> {
  chain = chain.then(async () => {
    const before = getPrefs();
    if (!before.appearanceSync.pending) return;
    const sent = accountFields(before.appearance);
    try {
      const saved = await api<UserPreferences>('/api/auth/me/preferences', { method: 'PUT', body: { appearance: sent } });
      // Only clear "pending" if nothing changed while the request was in flight.
      const stillSame = JSON.stringify(accountFields(getPrefs().appearance)) === JSON.stringify(sent);
      updatePrefs((p) => ({ ...p, appearanceSync: { pending: !stillSame, serverUpdatedAt: saved.updatedAt } }));
      if (!stillSame) void saveAppearance();
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err;
      // Offline: stays pending and is saved after reconnecting.
    }
  });
  return chain;
}

export async function setAppearance(patch: Partial<DevicePrefs['appearance']>): Promise<void> {
  const touchesAccount = Object.keys(patch).some((k) => k !== 'headerPhotoFileId');
  updatePrefs((p) => ({
    ...p,
    appearance: { ...p.appearance, ...patch },
    appearanceSync: touchesAccount ? { ...p.appearanceSync, pending: true } : p.appearanceSync,
  }));
  if (touchesAccount) await saveAppearance();
}

const DEFAULT_APPEARANCE = JSON.stringify(appearancePreferencesSchema.parse({}));

/** Bring this device in line with the account's saved appearance (called whenever /me loads). */
export function reconcileAppearance(server: UserPreferences): void {
  const local = getPrefs();
  if (local.appearanceSync.pending) {
    // An unsaved change made on this device is the newest choice.
    void saveAppearance().catch(() => undefined);
    return;
  }
  if (!server.updatedAt) {
    // Nothing saved on the account yet: keep (and save) a look already chosen on this device.
    if (JSON.stringify(accountFields(local.appearance)) !== DEFAULT_APPEARANCE) {
      updatePrefs((p) => ({ ...p, appearanceSync: { ...p.appearanceSync, pending: true } }));
      void saveAppearance().catch(() => undefined);
    }
    return;
  }
  if (server.updatedAt !== local.appearanceSync.serverUpdatedAt) {
    updatePrefs((p) => ({
      ...p,
      appearance: { ...p.appearance, ...server.appearance },
      appearanceSync: { pending: false, serverUpdatedAt: server.updatedAt },
    }));
  }
}
